import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FaX } from 'react-icons/fa6'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { usePermission } from '../../../../hooks/usePermission'
import { SelectOption } from '../../../../reducers/appReducer'
import { getRolesSelectOptions } from '../../../../utils/getRolesSelectOptions'

interface FilterUsersFormModalProps {
  onClose: () => void
  onSetOnlyRemovedUsers: () => void
}

const filterUsersFormSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  role: z.string().optional(),
})

type FilterUsersFormData = z.infer<typeof filterUsersFormSchema>

export function FilterUsersFormModal({ onClose, onSetOnlyRemovedUsers }: FilterUsersFormModalProps) {
  const [rolesSelect, setRolesSelect] = useState<SelectOption[]>([])
  const { profile } = useAppSelector(state => state.auth)
  const { removeRestoreUser } = usePermission()

  const [searchParams, setSearchParams] = useSearchParams()

  const { register, handleSubmit, control } = useForm<FilterUsersFormData>({
    resolver: zodResolver(filterUsersFormSchema),
    defaultValues: {
      id: searchParams.get('id') || '',
      name: searchParams.get('name') || '',
      role: searchParams.get('role') || '',
    },
  })

  function handleFilterUsers(data: FilterUsersFormData) {
    setSearchParams(state => {
      if (data.id) {
        state.set('id', data.id)
      } else {
        state.delete('id')
      }
      if (data.name) {
        state.set('name', data.name)
      } else {
        state.delete('name')
      }
      if (data.role) {
        state.set('role', data.role)
      } else {
        state.delete('role')
      }

      state.set('page', '1')
      return state
    })

    onClose()
  }

  function setOnlyremovedUsers() {
    onSetOnlyRemovedUsers()
    onClose()
  }

  const defineRolesSelect = useCallback(() => {
    setRolesSelect(getRolesSelectOptions(profile?.role || 'PROMOTER'))
  }, [profile])

  useEffect(() => {
    defineRolesSelect()
  }, [defineRolesSelect])

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] max-h-[80dvh] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold">
            Filtros de usuários
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full"
          onSubmit={handleSubmit(handleFilterUsers)}
        >
          <div>
            <Input
              placeholder="ID"
              id="id"
              label="Id do usuário"
              {...register('id')}
            />
          </div>
          <div>
            <Input
              label="Nome"
              id="name"
              placeholder="Fulano de tal"
              {...register('name')}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="role"
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="state"
                  >
                    Cargo
                  </label>
                  <Select
                    id="role"
                    options={rolesSelect}
                    onChange={state => onChange(state?.value)}
                    value={rolesSelect.filter(role => role.value === value)}
                    placeholder="Selecione..."
                  />
                </>
              )}
            />
          </div>
          <div>
            {removeRestoreUser &&
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={setOnlyremovedUsers}
              >
                Usuários removidos
              </Button>}
          </div>
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
            >
              Filtrar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
